import { t } from "i18next";
import { AlertCircle, CheckCircle2, Clock } from "lucide-react";

import { Badge } from "@/core/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { PayrollBatchDto } from "@/features/payroll/api/dto";
import type { PayrollBatchStatus } from "@/features/payroll/api/enums";
import { AmountMinor } from "@/features/shared/components/amount-minor";

const STATUS_VARIANTS: Record<
  PayrollBatchStatus,
  "default" | "secondary" | "success" | "warning" | "destructive"
> = {
  DRAFT: "secondary",
  SIMULATED: "default",
  PROCESSING: "warning",
  COMPLETED: "success",
  PARTIALLY_COMPLETED: "warning",
  FAILED: "destructive",
  CANCELLED: "secondary",
};

/**
 * Carte de synthèse d'une paie — label, statut, totaux, débit prévu, solde
 * disponible, fenêtre `simulationExpiresAt` et avertissements (solde
 * insuffisant, simulation expirée).
 */
export function PayrollBatchSummaryCard({ batch }: { batch: PayrollBatchDto }) {
  const expired =
    batch.status === "SIMULATED" &&
    batch.simulationExpiresAt &&
    new Date(batch.simulationExpiresAt).getTime() < Date.now();

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="text-lg">
            <NoTranslate as="span">{batch.label}</NoTranslate>
          </CardTitle>
          <CardDescription>
            {t("payroll.batches.detail.subtitle")} ·{" "}
            {t(`payroll.enums.feePayer.${batch.feePayer}`)}
          </CardDescription>
        </div>
        <Badge variant={STATUS_VARIANTS[batch.status] ?? "outline"}>
          {t(`payroll.enums.batchStatus.${batch.status}`)}
        </Badge>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
        <div>
          <p className="text-xs text-muted-foreground">
            {t("payroll.batches.detail.lineCount")}
          </p>
          <NoTranslate as="span" className="text-base font-semibold">
            {batch.paidCount} / {batch.lineCount}
          </NoTranslate>
          {batch.invalidCount > 0 && (
            <p className="mt-0.5 text-xs text-destructive">
              {t("payroll.batches.detail.invalidCount")} :{" "}
              <NoTranslate as="span">{batch.invalidCount}</NoTranslate>
            </p>
          )}
        </div>
        <div>
          <p className="text-xs text-muted-foreground">
            {t("payroll.batches.detail.grossAmount")}
          </p>
          <AmountMinor
            amountMinor={batch.grossAmountMinor}
            currency={batch.currency}
            className="text-base font-semibold"
          />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">
            {t("payroll.batches.detail.feeAmount")}
          </p>
          <AmountMinor
            amountMinor={batch.feeAmountMinor}
            currency={batch.currency}
            className="text-base font-semibold"
          />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">
            {t("payroll.batches.detail.debitAmount")}
          </p>
          <AmountMinor
            amountMinor={batch.debitAmountMinor}
            currency={batch.currency}
            className="text-base font-semibold"
          />
        </div>

        {batch.status === "SIMULATED" && (
          <>
            <div className="col-span-2 md:col-span-2">
              <p className="text-xs text-muted-foreground">
                {t("payroll.batches.detail.availableBalance")}
              </p>
              <div className="flex items-center gap-2">
                <AmountMinor
                  amountMinor={batch.availableBalanceMinor}
                  currency={batch.currency}
                />
                {batch.sufficientBalance ? (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {t("payroll.batches.detail.sufficient")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {t("payroll.batches.detail.insufficient")}
                  </span>
                )}
              </div>
            </div>
            <div className="col-span-2 md:col-span-2">
              <p className="text-xs text-muted-foreground">
                {t("payroll.batches.detail.simulationExpiresAt")}
              </p>
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <NoTranslate as="span" className="text-sm">
                  {formatDateWithFallback(batch.simulationExpiresAt, "—")}
                </NoTranslate>
                {expired && (
                  <span className="text-xs text-destructive">
                    · {t("payroll.batches.detail.simulationExpired")}
                  </span>
                )}
              </div>
            </div>
          </>
        )}

        {batch.disbursementReference && (
          <div className="col-span-2 md:col-span-4">
            <p className="text-xs text-muted-foreground">
              {t("payroll.batches.detail.disbursementReference")}
            </p>
            <NoTranslate as="span" className="font-mono text-sm">
              {batch.disbursementReference}
            </NoTranslate>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
